import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Camera, Clock, ArrowRight, Users, Snowflake, ChefHat } from "lucide-react";
import { getPendingBefore, clearPendingBefore, getStreak } from "@/lib/device";
import InstallPrompt from "@/components/InstallPrompt";
import HomeScene from "@/components/HomeScene";
import SoundToggle from "@/components/SoundToggle";

export default function Home() {
  const navigate = useNavigate();
  const [pending, setPending] = useState(null);
  const [streak, setStreak] = useState(0);

  useEffect(() => {
    setPending(getPendingBefore());
    setStreak(getStreak());
  }, []);

  return (
    <div className="app-shell flex flex-col px-6 pt-[calc(env(safe-area-inset-top)+3.5rem)] pb-[calc(env(safe-area-inset-bottom)+2.5rem)] relative overflow-hidden" data-testid="home-screen">
      <HomeScene />
      <SoundToggle className="absolute top-[calc(env(safe-area-inset-top)+1rem)] right-6 z-10" />

      {streak > 0 && (
        <motion.div
          className="inline-flex items-center gap-2 glass rounded-full px-3 py-1.5 self-start mb-6"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          data-testid="streak-chip"
        >
          <Snowflake className="w-4 h-4 text-[#4a5c62]" strokeWidth={1.5} />
          <span className="text-xs font-semibold text-[#16242a] tabular">{streak} card{streak > 1 ? "s" : ""} shared</span>
        </motion.div>
      )}

      <motion.header initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <p className="text-xs uppercase tracking-[0.28em] font-semibold text-[#4a5c62]">A domestic investigation</p>
        <h1 className="font-serif text-5xl sm:text-6xl font-black leading-[0.95] tracking-tighter mt-4">
          What's in
          <br />
          the Fridge
        </h1>
        <p className="text-base leading-relaxed text-[#4a5c62] mt-6 max-w-[22rem]">
          Snap it now. Snap it later. We'll tell you — dryly — what vanished, what's
          new, and what's been squatting in there for weeks.
        </p>
      </motion.header>

      <motion.div
        className="mt-12 flex-1 flex flex-col justify-center gap-4"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.15 }}
      >
        {pending ? (
          <BeforeReady
            pending={pending}
            onAfter={() => navigate("/capture?role=after")}
            onDiscard={() => {
              clearPendingBefore();
              setPending(null);
            }}
          />
        ) : (
          <button
            data-testid="start-before-btn"
            onClick={() => navigate("/capture?role=before")}
            className="btn-ink w-full rounded-sm px-6 py-5 flex items-center justify-between text-left"
          >
            <span>
              <span className="block font-serif text-2xl">Take the "before"</span>
              <span className="block text-sm opacity-70 mt-1">Photo A · the baseline fridge</span>
            </span>
            <Camera className="w-6 h-6 shrink-0" strokeWidth={1.5} />
          </button>
        )}
      </motion.div>

      <motion.div className="mt-8 flex flex-col gap-3" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }}>
        <button
          data-testid="cook-link"
          onClick={() => navigate("/cook")}
          className="w-full glass rounded-sm px-5 py-4 flex items-center justify-between text-left hover:bg-white/60 transition-colors"
        >
          <span className="flex items-center gap-3 text-[#16242a]">
            <ChefHat className="w-5 h-5 text-[#4a5c62]" strokeWidth={1.5} />
            <span className="text-sm font-medium">What can I cook?</span>
          </span>
          <ArrowRight className="w-4 h-4 text-[#4a5c62]" strokeWidth={1.5} />
        </button>
        <button
          data-testid="group-link"
          onClick={() => navigate("/group")}
          className="w-full glass rounded-sm px-5 py-4 flex items-center justify-between text-left hover:bg-white/60 transition-colors"
        >
          <span className="flex items-center gap-3 text-[#16242a]">
            <Users className="w-5 h-5 text-[#4a5c62]" strokeWidth={1.5} />
            <span className="text-sm font-medium">Share a fridge with flatmates</span>
          </span>
          <ArrowRight className="w-4 h-4 text-[#4a5c62]" strokeWidth={1.5} />
        </button>
        <button
          data-testid="timeline-link"
          onClick={() => navigate("/timeline")}
          className="w-full glass rounded-sm px-5 py-4 flex items-center justify-between text-left hover:bg-white/60 transition-colors"
        >
          <span className="flex items-center gap-3 text-[#16242a]">
            <Clock className="w-5 h-5 text-[#4a5c62]" strokeWidth={1.5} />
            <span className="text-sm font-medium">The case files</span>
          </span>
          <ArrowRight className="w-4 h-4 text-[#4a5c62]" strokeWidth={1.5} />
        </button>
      </motion.div>

      <section className="mt-16 relative z-10 text-[#4a5c62] leading-relaxed" data-testid="home-about">
        <h2 className="font-serif text-2xl text-[#16242a] tracking-tight">The fridge detective, at your service</h2>
        <p className="text-sm mt-3">
          What's in the Fridge is a playful little tool that turns two photos of your fridge into a verdict. Snap
          the shelves now, snap them again after the weekend raid, and our vision engine compares the two: what
          vanished, what turned up, and what's been quietly squatting behind the pickles for a fortnight. The copy
          is dry, warm and British-leaning — because the fun is the point.
        </p>
        <h3 className="font-serif text-lg text-[#16242a] mt-6">How it works</h3>
        <p className="text-sm mt-2">
          Take a "before" photo, carry on with life, then take an "after" photo — a ghost overlay helps you line up
          the shot. We analyse both and reveal the results card by card, scaling the tone to how confident we are.
          High confidence gets a bold one-liner; a hunch gets a hedge, because the humour absorbs the guesswork.
          Every verdict ends with a shareable card in your choice of styles, and a Fridge Personality — are you The
          Hoarder, The Purger, or The Minimalist?
        </p>
        <h3 className="font-serif text-lg text-[#16242a] mt-6">More than a toy</h3>
        <p className="text-sm mt-2">
          Point the camera at a full fridge and tap "What can I cook?" to get three recipe ideas built around what
          you actually have — filtered by diet, effort and mood — each with a "you have" versus "you'll need" split
          and a combined shopping list. Share a fridge with flatmates so accusations fly both ways, tag the culprit
          who finished the milk in Accusation Mode, and keep a light timeline of past snaps and your longest-serving
          leftover. No account needed — it works straight from a shared link, right in your browser.
        </p>
        <p className="text-sm mt-2">
          It's mobile-first and installs to your home screen like a proper app, so the next time the flat descends on
          the leftovers, the evidence is only a tap away. Free to use, quick to share, and genuinely handy for
          cutting down on food waste — even if the jokes are the reason you keep coming back.
        </p>
      </section>

      <InstallPrompt />
    </div>
  );
}

function BeforeReady({ pending, onAfter, onDiscard }) {
  return (
    <div className="flex flex-col gap-4" data-testid="before-ready">
      <div className="glass rounded-sm p-4 flex items-center gap-4">
        <img src={pending.dataUrl} alt="before" className="w-20 h-24 object-cover rounded-sm border border-[#c4d2d5] bg-[#dbe6e9]" />
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4a5c62]">Before is on file</p>
          <p className="font-serif text-xl mt-1 leading-tight">The trap is set.</p>
          <p className="text-sm text-[#4a5c62] mt-1">
            {pending.fridgeCode ? `For fridge ${pending.fridgeCode}. ` : ""}Come back after a raid and snap the after.
          </p>
        </div>
      </div>
      <button data-testid="start-after-btn" onClick={onAfter} className="btn-ink w-full rounded-sm px-6 py-5 flex items-center justify-between text-left">
        <span>
          <span className="block font-serif text-2xl">Take the "after"</span>
          <span className="block text-sm opacity-70 mt-1">Photo B · then we compare</span>
        </span>
        <Camera className="w-6 h-6 shrink-0" strokeWidth={1.5} />
      </button>
      <button data-testid="discard-before-btn" onClick={onDiscard} className="text-sm text-[#4a5c62] underline underline-offset-4 self-start">
        Start over with a fresh before
      </button>
    </div>
  );
}
